import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useProducts from "hooks/useProducts";
import useCustomers from "hooks/useCustomers";
import useSuppliers from "hooks/useSuppliers";
import useTransactions from "hooks/useTransactions";
import { FC, useEffect, useRef, useState } from "react";
import AmountTag from "components/tags/amountTag";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import Callout from "components/callout/callout";
import useCategories from "hooks/useCategories";
import useLocations from "hooks/useLocations";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import BreadCrumbsMenu from "components/buttons/breadcrumbsMenu";
import TableList from "components/list/tableList/tableList";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";
import { APP_CONFIG } from "../../constants/constants";
import LoadMore from "../list/loadMore";
import { ElementPosition, FormStatus } from "@/types";
import SearchResultHolder from "./SearchResultHolder";
import Image from "../image/Image";
import useStorages from "@/hooks/useStorages";
import useLayout from "@/hooks/useLayout";
import { SEARCH_NAVIGATION_STATE, trackSearch, trackSearchNavigate } from "@/services/analytics";

interface SeachFieldProps {
    storageId: string
    categoryId?: string
    locationId?: string
    customerId?: string
    supplierId?: string
    productId?: string
    transactionId?: string
    className?: string
    selectedProductCode?: string
    onProductSelect?: (productId: string) => void
    placeholder?: string
    displayBreadcrumbs?: boolean
}

enum DocumentType {
    PRODUCTS = 'products',
    CUSTOMERS = 'customers',
    SUPPLIERS = 'suppliers',
    CATEGORIES = 'categories',
    LOCATIONS = 'locations',
    TRANSACTIONS = 'transactions'
}

const Search: FC<SeachFieldProps> = ({ 
    storageId, 
    categoryId, 
    locationId,
    customerId, 
    supplierId,
    productId,
    transactionId,
    className,
    placeholder,
    displayBreadcrumbs = true
}) => {

    const { t } = useTranslation();

    const [inFocus, setInFocus] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const SearchWrapperRef = useRef<HTMLDivElement>(null);
    const SearchResultsRef = useRef<HTMLDivElement>(null);
    const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);
    const [documentType, setDocumentType] = useState<DocumentType>(DocumentType.PRODUCTS);
    const { openDialog, searchDisplayDeleted } = useLayout();

    const {
        isCustomersAndSuppliersEnabled,
        isTransactionsEnabled,
        storage
    } = useStorages({ storageId });

    const {
        filteredProducts,
        filterQuery: productFilterQuery,
        setFilterQuery: setProductFilterQuery,
        products,
        hasNextPage: productsHasNextPage,
        fetchNextPage: productsFetchNextPage,
        productsStatus,
        queryMatchingAttributes,
        displayDeleted: productsDisplayDeleted,
        setDisplayDeleted: setProductsDisplayDeleted
    } = useProducts({ storageId });

    // Filter out draft products when in selector mode (for actions)
    const selectableProducts = filteredProducts;

    const { 
        getCategoryNamesString, 
        findCategory, 
        filteredCategories, 
        filterQuery: categoriesFilterQuery, 
        setFilterQuery: setCategoriesFilterQuery,
        displayDeleted: categoriesDisplayDeleted,
        setDisplayDeleted: setCategoriesDisplayDeleted
    } = useCategories({ storageId });

    const { 
        getLocationNamesString, 
        filteredLocations, 
        filterQuery: locationsFilterQuery, 
        setFilterQuery: setLocationsFilterQuery,
        displayDeleted: locationsDisplayDeleted,
        setDisplayDeleted: setLocationsDisplayDeleted
    } = useLocations({ storageId });

    const { 
        filteredCustomers, 
        filterQuery: customersFilterQuery, 
        setFilterQuery: setCustomersFilterQuery,
        displayDeleted: customersDisplayDeleted,
        setDisplayDeleted: setCustomersDisplayDeleted
    } = useCustomers({ storageId });

    const { 
        filteredSuppliers, 
        filterQuery: suppliersFilterQuery, 
        setFilterQuery: setSuppliersFilterQuery,
        displayDeleted: suppliersDisplayDeleted,
        setDisplayDeleted: setSuppliersDisplayDeleted
    } = useSuppliers({ storageId });

    const { filteredTransactions, filterQuery: transactionsFilterQuery, setFilterQuery: setTransactionsFilterQuery, hasNextPage: transactionsHasNextPage, getMore: transactionsFetchNextPage, transactionsStatus } = useTransactions({ storageId });

    // Unified filter query based on document type
    const filterQuery = documentType === DocumentType.PRODUCTS ? productFilterQuery :
                        documentType === DocumentType.CUSTOMERS ? customersFilterQuery :
                        documentType === DocumentType.SUPPLIERS ? suppliersFilterQuery :
                        documentType === DocumentType.CATEGORIES ? categoriesFilterQuery :
                        documentType === DocumentType.LOCATIONS ? locationsFilterQuery :
                        transactionsFilterQuery;

    const setFilterQuery = documentType === DocumentType.PRODUCTS ? setProductFilterQuery :
                           documentType === DocumentType.CUSTOMERS ? setCustomersFilterQuery :
                           documentType === DocumentType.SUPPLIERS ? setSuppliersFilterQuery :
                           documentType === DocumentType.CATEGORIES ? setCategoriesFilterQuery :
                           documentType === DocumentType.LOCATIONS ? setLocationsFilterQuery :
                           setTransactionsFilterQuery;

    // Synchronize hook-level displayDeleted flags with global search settings
    useEffect(() => {
        setProductsDisplayDeleted(searchDisplayDeleted);
        setCustomersDisplayDeleted(searchDisplayDeleted);
        setSuppliersDisplayDeleted(searchDisplayDeleted);
        setCategoriesDisplayDeleted(searchDisplayDeleted);
        setLocationsDisplayDeleted(searchDisplayDeleted);
    }, [searchDisplayDeleted, setProductsDisplayDeleted, setCustomersDisplayDeleted, setSuppliersDisplayDeleted, setCategoriesDisplayDeleted, setLocationsDisplayDeleted]);

    // Get selected product for display in selector mode
    const selectedProduct =  undefined;

    // Check if any filtered product has location or categories (only if there's a query)
    const hasLocation = filterQuery.length > 0 && selectableProducts.some(product =>
        (product.locations && product.locations.length > 0) || (product.location && product.location.trim() !== '')
    );
    const hasCategories = filterQuery.length > 0 && selectableProducts.some(product => product.categories && product.categories.length > 0);

    const handleOnClick = () => {
        setInFocus(true);
        if (SearchWrapperRef.current) {
            const rect = SearchWrapperRef.current.getBoundingClientRect();
            setInputLocation({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    }
    const handleOnBlur = () => {
        setInFocus(false);
    }
    const handleSearchIconClick = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }

    const location = useLocation();

    useEffect(() => {
        handleOnBlur();
        // eslint-disable-next-line
    }, [location]);

    useEffect(() => {
        const query = filterQuery.trim();
        if (!query) return;

        const timeout = window.setTimeout(() => {
            trackSearch({ contentType: documentType, searchTerm: query });
        }, 600);

        return () => window.clearTimeout(timeout);
    }, [filterQuery, documentType]);

    // Update rect on window resize if inFocus
    useEffect(() => {
        if (!inFocus) return;
        const updateRect = () => {
            if (SearchWrapperRef.current) {
                const rect = SearchWrapperRef.current.getBoundingClientRect();
                setInputLocation({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                });
            }
        };
        window.addEventListener('resize', updateRect);
        updateRect();
        return () => window.removeEventListener('resize', updateRect);
    }, [inFocus]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // We only trigger if the element is not the search field or search results
            const target = e.target as Node;
            if (
                SearchWrapperRef.current && !SearchWrapperRef.current.contains(target) &&
                SearchResultsRef.current && !SearchResultsRef.current.contains(target)
            ) {
                handleOnBlur();
            }
        }

        if (inFocus) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inFocus]);

    const Search = selectedProduct ? null : (
        <div ref={SearchWrapperRef} className={`relative md:mx-auto ${className ? className : ''}`}>
            <div className={`
                relative w-full flex items-center gap-1
                rounded-md border border-gray-300 bg-white
                ${inFocus ? `border-gray-500 border-b-white rounded-b-none` : ``}
                ${!storageId ? `bg-gray-200` : ``}
            `}>
                <SearchIcon 
                    className={`ml-3 shrink-0 ${storageId ? `cursor-pointer` : `cursor-not-allowed`} fill-gray-500`}
                    onClick={handleSearchIconClick}
                />
                <input 
                    type={`text`}
                    placeholder={inFocus ? (placeholder || t('placholderSearchQuery')) : ``}
                    className={`
                        flex-1 min-w-0
                        border-none rounded-none
                        bg-transparent
                        focus:outline-none
                        pl-2 pr-2
                        disabled:bg-transparent
                    `} 
                    value={inFocus ? filterQuery : ``}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    data-ignore={`true`}
                    onFocus={handleOnClick}
                    ref={searchInputRef}
                    disabled={!storageId}
                />
                {inFocus && filterQuery.length > 0 &&
                    <CloseIcon 
                        className={`shrink-0 cursor-pointer fill-black`} 
                        onClick={() => setFilterQuery('')}
                    />
                }
                {displayBreadcrumbs && !inFocus && <div className="absolute top-0 h-full flex items-center bg-green -translate-x-1/2 left-1/2 pointer-events-auto">
                    <BreadCrumbsMenu 
                        storageId={storageId}
                        categoryId={categoryId}
                        locationId={locationId}
                        customerId={customerId}
                        supplierId={supplierId}
                        productId={productId}
                        transactionId={transactionId}
                    />
                </div>}
                {/* Selector to choose search target and options button */}
                {storageId && (
                    <div className="h-full flex items-center gap-2 pr-2 shrink-0">
                        <select
                            className={`
                                !h-10 !w-auto border-none
                                focus:outline-none px-2 cursor-pointer
                                text-gray-500 text-right bg-transparent
                            `}
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                        >
                            <option value={DocumentType.PRODUCTS}>{t('labelProducts')}</option>
                            {isCustomersAndSuppliersEnabled() && <option value={DocumentType.CUSTOMERS}>{t('labelCustomers')}</option>}
                            {isCustomersAndSuppliersEnabled() && <option value={DocumentType.SUPPLIERS}>{t('labelSuppliers')}</option>}
                            <option value={DocumentType.CATEGORIES}>{t('labelCategories')}</option>
                            <option value={DocumentType.LOCATIONS}>{t('labelLocations')}</option>
                            {isTransactionsEnabled() && <option value={DocumentType.TRANSACTIONS}>{t('labelTransactions')}</option>}
                        </select>
                    </div>
                )}
            </div>
        </div>
    )

    return (
        <>
            {/* Search Field */}
            {Search}
            {/* Search Results */}
            {!selectedProduct && inFocus && inputLocation && (
                <SearchResultHolder
                    inputRef={inputLocation}
                    resultsRef={SearchResultsRef}
                >
                    {documentType === DocumentType.PRODUCTS && (
                        <>
                            {!productFilterQuery.length && storage?.counters?.productCounter === 0 ? <Callout className={`rounded-none`} type="info">{t('calloutNoProductsExist')}</Callout> : null}
                            {productFilterQuery.length && !selectableProducts.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {selectableProducts.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell></TableListCell>
                                        <TableListCell>{t('labelSKU')}</TableListCell>
                                        <TableListCell>{t('name')}</TableListCell>
                                        <TableListCell></TableListCell>
                                        {hasLocation && <TableListCell>{t('location')}</TableListCell>}
                                        {hasCategories && <TableListCell>{t('labelCategory')}</TableListCell>}
                                        {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                            if (categoryId === "_") return null
                                            const categoryName = findCategory(categoryId)?.name
                                            if (customInputNames.length > 0) {
                                                return customInputNames.map((customInputName, key) => (
                                                    <TableListCell key={key} className="capitalize" direction="vertical">
                                                        <span className={`text-gray-500`}>{categoryName}</span><span>{customInputName}</span>
                                                    </TableListCell>
                                                ))
                                            }
                                            return null
                                        })}
                                    </TableListRow>
                                    {selectableProducts.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((product, key) => {
                                        if (!product.id) return null

                                        return (
                                            <TableListRow
                                                key={key}
                                                to={ `/storage/products/${product.id}`}
                                                state={SEARCH_NAVIGATION_STATE}
                                                onClick={() => trackSearchNavigate({ contentType: DocumentType.PRODUCTS, itemId: product.id })}
                                                isDeleted={!!product.isDeleted}
                                            >
                                                <TableListCell>
                                                    {product.images && product.images.length > 0 ? (
                                                        <Image 
                                                            src={product.images[0].url} 
                                                            alt={product.name}
                                                            size="sm"
                                                        />
                                                    ) : <Image size="sm" />}
                                                </TableListCell>
                                                <TableListCell>{product.sku}</TableListCell>
                                                <TableListCell>{product.name}</TableListCell>
                                                <TableListCell>
                                                    <AmountTag amount={Number(product.status?.currentAmount)} unit={product.unit} lowStockWarning={product.stockSettings?.lowStockWarning} />
                                                </TableListCell>
                                                {hasLocation && <TableListCell>{getLocationNamesString(product.locations || []) || product.location}</TableListCell>}
                                                {hasCategories && <TableListCell>{getCategoryNamesString(product.categories)}</TableListCell>}
                                                {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                                    if (categoryId === "_" || !product.customInputs) return null
                                                    const customInputCategory = product.customInputs[categoryId]
                                                    if (customInputNames.length > 0) {
                                                        return customInputNames.map((customInputName, key) => {
                                                            if (!customInputCategory) return <TableListCell key={key}>-</TableListCell>
                                                            return <TableListCell key={key}>{customInputCategory[customInputName]}</TableListCell>
                                                        })
                                                    } else {
                                                        return null
                                                    }
                                                })}
                                            </TableListRow>
                                        )
                                    })}
                                </TableList>
                            ) : null}
                            <LoadMore hasNextPage={productsHasNextPage} action={productsFetchNextPage} isLoading={productsStatus === FormStatus.LOADING} trigger={products} />
                        </>
                    )}

                    {documentType === DocumentType.CUSTOMERS && (
                        <>
                            {!customersFilterQuery.length && storage?.counters?.customerCounter === 0 ? <Callout className={`rounded-none`} type="info">{t('calloutNoCustomersExist')}</Callout> : null}
                            {customersFilterQuery.length && !filteredCustomers.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {filteredCustomers.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell>{t('id')}</TableListCell>
                                        <TableListCell>{t('name')}</TableListCell>
                                        <TableListCell>{t('email')}</TableListCell>
                                        <TableListCell>{t('phone')}</TableListCell>
                                    </TableListRow>
                                    {filteredCustomers.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((customer, key) => (
                                        <TableListRow
                                            key={key}
                                             to={`/storage/customers/${customer.id}`}
                                            state={SEARCH_NAVIGATION_STATE}
                                            onClick={() => trackSearchNavigate({ contentType: DocumentType.CUSTOMERS, itemId: customer.id })}
                                            isDeleted={!!customer.isDeleted}
                                        >
                                            <TableListCell>{customer.customerId}</TableListCell>
                                            <TableListCell>{customer.name}</TableListCell>
                                            <TableListCell>{customer.contactInformation.email || '-'}</TableListCell>
                                            <TableListCell>{customer.contactInformation.phone || '-'}</TableListCell>
                                        </TableListRow>
                                    ))}
                                </TableList>
                            ) : null}
                        </>
                    )}

                    {documentType === DocumentType.SUPPLIERS && (
                        <>
                            {!suppliersFilterQuery.length && storage?.counters?.supplierCounter === 0 ? <Callout className={`rounded-none`} type="info">{t('calloutNoSuppliersExist')}</Callout> : null}
                            {suppliersFilterQuery.length && !filteredSuppliers.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {filteredSuppliers.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell>{t('id')}</TableListCell>
                                        <TableListCell>{t('name')}</TableListCell>
                                        <TableListCell>{t('email')}</TableListCell>
                                        <TableListCell>{t('labelPhone')}</TableListCell>
                                    </TableListRow>
                                    {filteredSuppliers.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((supplier, key) => (
                                        <TableListRow
                                            key={key}
                                            to={`/storage/suppliers/${supplier.id}`}
                                            state={SEARCH_NAVIGATION_STATE}
                                            onClick={() => trackSearchNavigate({ contentType: DocumentType.SUPPLIERS, itemId: supplier.id })}
                                            isDeleted={!!supplier.isDeleted}
                                        >
                                            <TableListCell>{supplier.supplierId}</TableListCell>
                                            <TableListCell>{supplier.name}</TableListCell>
                                            <TableListCell>{supplier.contactInformation?.email || '-'}</TableListCell>
                                            <TableListCell>{supplier.contactInformation?.phone || '-'}</TableListCell>
                                        </TableListRow>
                                    ))}
                                </TableList>
                            ) : null}
                        </>
                    )}

                    {documentType === DocumentType.CATEGORIES && (
                        <>
                            {!categoriesFilterQuery.length && storage?.counters?.categoryCounter === 0 ? <Callout className={`rounded-none`} type="info">{t('calloutNoCategoriesExist')}</Callout> : null}
                            {categoriesFilterQuery.length && !filteredCategories.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {filteredCategories.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell>{t('categoryId')}</TableListCell>
                                        <TableListCell>{t('name')}</TableListCell>
                                        <TableListCell>{t('description')}</TableListCell>
                                    </TableListRow>
                                    {filteredCategories.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((category, key) => (
                                        <TableListRow
                                            key={key}
                                            to={`/storage/categories/${category.id}`}
                                            state={SEARCH_NAVIGATION_STATE}
                                            onClick={() => trackSearchNavigate({ contentType: DocumentType.CATEGORIES, itemId: category.id })}
                                            isDeleted={!!category.isDeleted}
                                        >
                                            <TableListCell>{category.categoryId}</TableListCell>
                                            <TableListCell>{category.name}</TableListCell>
                                            <TableListCell>{category.description || '-'}</TableListCell>
                                        </TableListRow>
                                    ))}
                                </TableList>
                            ) : null}
                        </>
                    )}

                    {documentType === DocumentType.LOCATIONS && (
                        <>
                            {!locationsFilterQuery.length && !filteredLocations.length ? <Callout className={`rounded-none`} type="info">{t('calloutNoLocationsExist')}</Callout> : null}
                            {locationsFilterQuery.length && !filteredLocations.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {filteredLocations.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell>{t('name')}</TableListCell>
                                        <TableListCell>{t('description')}</TableListCell>
                                    </TableListRow>
                                    {filteredLocations.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((item, key) => (
                                        <TableListRow
                                            key={key}
                                            to={`/storage/locations/${item.id}`}
                                            state={SEARCH_NAVIGATION_STATE}
                                            onClick={() => trackSearchNavigate({ contentType: DocumentType.LOCATIONS, itemId: item.id })}
                                            isDeleted={!!item.isDeleted}
                                        >
                                            <TableListCell>{item.name}</TableListCell>
                                            <TableListCell>{item.description || '-'}</TableListCell>
                                        </TableListRow>
                                    ))}
                                </TableList>
                            ) : null}
                        </>
                    )}

                    {documentType === DocumentType.TRANSACTIONS && (
                        <>
                            {!transactionsFilterQuery.length && storage?.counters?.transactionCounter === 0 ? <Callout className={`rounded-none`} type="info">{t('calloutNoTransactionsExist')}</Callout> : null}
                            {transactionsFilterQuery.length && !filteredTransactions.length ? <Callout className={`rounded-none`} type="error">{t('noResultsFound')}</Callout> : null}

                            {filteredTransactions.length ? (
                                <TableList className="small-list hover-effect border-none rounded-t-none">
                                    <TableListRow isHeader>
                                        <TableListCell>{t('transactionId')}</TableListCell>
                                        <TableListCell>{t('type')}</TableListCell>
                                        <TableListCell>{t('customer')}</TableListCell>
                                        <TableListCell>{t('description')}</TableListCell>
                                    </TableListRow>
                                    {filteredTransactions.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((transaction, key) => (
                                        <TableListRow
                                            key={key}
                                            to={`/storage/transactions/${transaction.id}`}
                                            state={SEARCH_NAVIGATION_STATE}
                                            onClick={() => trackSearchNavigate({ contentType: DocumentType.TRANSACTIONS, itemId: transaction.id })}
                                        >
                                            <TableListCell>{transaction.transactionId}</TableListCell>
                                            <TableListCell className="capitalize">{transaction.type}</TableListCell>
                                            <TableListCell>{transaction.customer?.customerName || '-'}</TableListCell>
                                            <TableListCell>{transaction.description || '-'}</TableListCell>
                                        </TableListRow>
                                    ))}
                                </TableList>
                            ) : null}
                            <LoadMore hasNextPage={transactionsHasNextPage} action={transactionsFetchNextPage} isLoading={transactionsStatus === FormStatus.LOADING} trigger={filteredTransactions} />
                        </>
                    )}
                </SearchResultHolder>
            )}
        </>
    )
}

export default Search

